import { createAsyncThunk } from "@reduxjs/toolkit"
import PapaParse from "papaparse"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config"
import {
    PoliciesExportDestination,
    PoliciesListType,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import type { IPagination } from "@common-shared/types/general.types"
import {
    ICoverageHistoryAggregationsFlags,
    ICoverageHistoryAggregationsMedicaidActivePlanType,
    ICoverageHistoryAggregationsMedicaidPlanType,
    ICoverageHistoryAggregationsStatusCodes,
    ICoverageHistoryListSort,
} from "@common-shared/types/internalApis/coverageHistory.types"
import { IPoliciesListFiltersData } from "@common-shared/types/policies.types"
import { IOneVerifyGetHistoryListAdditionalData } from "@common-shared/types/requestData/oneVerifyRequestData.types"
import {
    ICoverageHistoryAggregationsPlanYearResets,
    IOneVerifyCoverageCheckHistory,
    IOneVerifyCoverageResult,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import { ISelectRenderedOption } from "@front-shared/types/objects.types"

import { CHECKER_CONFIG } from "../../config/checker.config"
import { GENERAL_CONFIG } from "../../config/general.config"
import { POLICIES_CONFIG } from "../../config/policies.config"
import { AmplitudeHelper } from "../../helpers/amplitude.helper"
import { CheckerHelper } from "../../helpers/checker.helper"
import { CsvHelper } from "../../helpers/csv.helper"
import { ExportReportHelper } from "../../helpers/exportReport.helper"
import { PermissionsHelper } from "../../helpers/permissions.helper"
import { PoliciesListHelper } from "../../helpers/policiesList.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { PoliciesService } from "../../services/policies.service"
import { TaskAssignmentsService } from "../../services/taskAssignments.service"
import type {
    ICoverageHistoryListResult,
    IPoliciesListFiltersDataWithMedicaid,
} from "../../types/data.types"
import type { IThunkActionWithPracticeData } from "../../types/general.types"
import { coverageOverridesSetSelectedPolicyOverride } from "../slices/coverageOverrides.slice"
import {
    policiesAddSelectedItemHistory,
    policiesIncreaseGetFullListProgress,
    policiesResetSelectedItem,
    policiesSetAgenticList,
    policiesSetAssignedTasksList,
    policiesSetGettingFullListProgress,
    policiesSetHistoryList,
    policiesSetMedicaidList,
    policiesSetOverridesList,
    policiesSetPlanYearResetsList,
    policiesSetReportsList,
    policiesSetSelectedItem,
    policiesUpdateListItems,
    policiesUpdatedSelectedItemEhrWriteStatus,
} from "../slices/policies.slice"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"
import type { RootState } from "../store"
import { taskAssignmentsGetList } from "./taskAssignments.thunks"
import { userDetailsGetPracticeUsageQuota } from "./userDetails.thunks"

export const policiesReloadList = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        policiesViewType: PoliciesListType
    }>
>("policiesReloadListPrefix", async (action, { dispatch, getState }) => {
    const globalState = getState() as RootState

    const {
        nextPagination,
        filtersConfiguration: filters,
        sortConfiguration: sort,
    } = PoliciesListHelper.getViewTypedStateData(
        action.payload.policiesViewType,
        globalState.policies,
    )

    const pagination = {
        ...nextPagination,

        // Reload same page
        start: Math.max(0, nextPagination.start - nextPagination.count),
    }

    switch (action.payload.policiesViewType) {
        case PoliciesListType.REPORTS:
            dispatch(
                policiesGetReportsList({
                    ...action,

                    payload: {
                        pagination,
                        filters,
                        sort,
                    },
                }),
            )

            break

        case PoliciesListType.OVERRIDES:
            dispatch(
                policiesGetOverridesList({
                    ...action,

                    payload: {
                        pagination,
                        filters,
                        sort,
                    },
                }),
            )

            break

        case PoliciesListType.HISTORY_SEARCH:
            if (UtilHelper.isEmptyObject(filters)) {
                return
            }

            dispatch(
                policiesGetHistoryList({
                    ...action,

                    payload: {
                        pagination,
                        filters,
                        sort,
                    },
                }),
            )

            break

        case PoliciesListType.PLAN_YEAR_RESETS:
            dispatch(
                policiesGetPlanYearResetsList({
                    ...action,

                    payload: {
                        pagination,
                        filters,
                        sort,
                    },
                }),
            )

            break

        case PoliciesListType.MEDICAID:
            dispatch(
                policiesGetMedicaidList({
                    ...action,

                    payload: {
                        pagination,
                        filters,
                        sort,
                    },
                }),
            )

            break

        case PoliciesListType.AGENTIC:
            dispatch(
                policiesGetAgenticList({
                    ...action,

                    payload: {
                        pagination,
                        filters,
                        sort,
                    },
                }),
            )

            break

        case PoliciesListType.TASK_ASSIGNMENTS:
        case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
        case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
            dispatch(
                taskAssignmentsGetList({
                    ...action,

                    payload: {
                        listType: action.payload.policiesViewType,
                        pagination,
                        filters,
                        sort,
                    },
                }),
            )

            break

        case PoliciesListType.ASSIGNED_TASKS:
        case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
        case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
            dispatch(
                policiesGetAssignedTasksList({
                    ...action,

                    payload: {
                        listType: action.payload.policiesViewType,
                        pagination,
                        filters,
                        sort,
                    },
                }),
            )

            break
    }
})

/**
 * Get list of policies for reports
 */
export const policiesGetReportsList = createAsyncThunk<
    ICoverageHistoryListResult,
    IThunkActionWithPracticeData<{
        pagination: IPagination
        filters?: IPoliciesListFiltersData
        sort?: ICoverageHistoryListSort
    }>
>("policiesGetReportsListPrefix", async (action, { dispatch, signal }) => {
    dispatch(runtimeStartLoading("policiesGetReportsListLoading"))

    const defaultResult: ICoverageHistoryListResult = {
        items: [],
        newPagination: action.payload.pagination,
        isError: false,
    }

    // reset items
    dispatch(policiesSetReportsList(defaultResult))

    try {
        const result = await PoliciesService.getReportsList(
            action.practice,
            action.payload.pagination,
            action.payload.filters,
            action.payload.sort,
            signal,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || defaultResult
    } catch (e) {
        action.onError?.()

        return defaultResult
    } finally {
        // Only stop loading if it wasn't aborted by other trigger
        if (!signal.aborted) {
            dispatch(runtimeStopLoading("policiesGetReportsListLoading"))
        }
    }
})

/**
 * Get list of historical policies
 */
export const policiesGetHistoryList = createAsyncThunk<
    ICoverageHistoryListResult,
    IThunkActionWithPracticeData<{
        pagination: IPagination
        filters?: IPoliciesListFiltersData
        sort?: ICoverageHistoryListSort
    }>
>("policiesGetHistoryListPrefix", async (action, { dispatch, signal }) => {
    dispatch(runtimeStartLoading("policiesGetHistoryListLoading"))

    const defaultResult: ICoverageHistoryListResult = {
        items: [],
        newPagination: action.payload.pagination,
        isError: false,
    }

    // reset items
    dispatch(policiesSetHistoryList(defaultResult))

    try {
        const result = await PoliciesService.getSearchList(
            action.practice,
            action.payload.pagination,
            action.payload.filters,
            action.payload.sort,
            signal,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || defaultResult
    } catch (e) {
        action.onError?.()

        return defaultResult
    } finally {
        // Only stop loading if it wasn't aborted by other trigger
        if (!signal.aborted) {
            dispatch(runtimeStopLoading("policiesGetHistoryListLoading"))
        }
    }
})

/**
 * Get list of overridden policies
 */
export const policiesGetOverridesList = createAsyncThunk<
    ICoverageHistoryListResult,
    IThunkActionWithPracticeData<{
        pagination: IPagination
        filters?: IPoliciesListFiltersData
        sort?: ICoverageHistoryListSort
    }>
>("policiesGetOverridesListPrefix", async (action, { dispatch, signal }) => {
    dispatch(runtimeStartLoading("policiesGetOverridesListLoading"))

    const defaultResult: ICoverageHistoryListResult = {
        items: [],
        newPagination: action.payload.pagination,
        isError: false,
    }

    // reset items
    dispatch(policiesSetOverridesList(defaultResult))

    try {
        const result = await PoliciesService.getOverridesList(
            action.practice,
            action.payload.pagination,
            action.payload.filters,
            action.payload.sort,
            signal,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || defaultResult
    } catch (e) {
        action.onError?.()

        return defaultResult
    } finally {
        // Only stop loading if it wasn't aborted by other trigger
        if (!signal.aborted) {
            dispatch(runtimeStopLoading("policiesGetOverridesListLoading"))
        }
    }
})

/**
 * Get list of plan year resets policies
 */
export const policiesGetPlanYearResetsList = createAsyncThunk<
    ICoverageHistoryListResult,
    IThunkActionWithPracticeData<{
        pagination: IPagination
        filters?: IPoliciesListFiltersData
        sort?: ICoverageHistoryListSort
    }>
>(
    "policiesGetPlanYearResetsListPrefix",
    async (action, { dispatch, signal }) => {
        dispatch(runtimeStartLoading("policiesGetPlanYearResetsListLoading"))

        const defaultResult: ICoverageHistoryListResult = {
            items: [],
            newPagination: action.payload.pagination,
            isError: false,
        }

        // reset items
        dispatch(policiesSetPlanYearResetsList(defaultResult))

        try {
            const result = await PoliciesService.getPlanYearResetsList(
                action.practice,
                action.payload.pagination,
                action.payload.filters,
                action.payload.sort,
                signal,
            )

            if (result) {
                action.onSuccess?.(result)
            } else {
                action.onError?.(result)
            }

            return result || defaultResult
        } catch (e) {
            action.onError?.()

            return defaultResult
        } finally {
            // Only stop loading if it wasn't aborted by other trigger
            if (!signal.aborted) {
                dispatch(
                    runtimeStopLoading("policiesGetPlanYearResetsListLoading"),
                )
            }
        }
    },
)

/**
 * Get list of medicaid
 */
export const policiesGetMedicaidList = createAsyncThunk<
    ICoverageHistoryListResult,
    IThunkActionWithPracticeData<{
        pagination: IPagination
        filters?: IPoliciesListFiltersData
        sort?: ICoverageHistoryListSort
    }>
>("policiesGetMedicaidListPrefix", async (action, { dispatch, signal }) => {
    dispatch(runtimeStartLoading("policiesGetMedicaidListLoading"))

    const defaultResult: ICoverageHistoryListResult = {
        items: [],
        newPagination: action.payload.pagination,
        isError: false,
    }

    // reset items
    dispatch(policiesSetMedicaidList(defaultResult))

    try {
        const result = await PoliciesService.getMedicaidList(
            action.practice,
            action.payload.pagination,
            action.payload.filters,
            action.payload.sort,
            signal,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || defaultResult
    } catch (e) {
        action.onError?.()

        return defaultResult
    } finally {
        // Only stop loading if it wasn't aborted by other trigger
        if (!signal.aborted) {
            dispatch(runtimeStopLoading("policiesGetMedicaidListLoading"))
        }
    }
})

/**
 * Get list of agentic checks
 */
export const policiesGetAgenticList = createAsyncThunk<
    ICoverageHistoryListResult,
    IThunkActionWithPracticeData<{
        pagination: IPagination
        filters?: IPoliciesListFiltersData
        sort?: ICoverageHistoryListSort
    }>
>("policiesGetAgenticListPrefix", async (action, { dispatch, signal }) => {
    dispatch(runtimeStartLoading("policiesGetAgenticListLoading"))

    const defaultResult: ICoverageHistoryListResult = {
        items: [],
        newPagination: action.payload.pagination,
        isError: false,
    }

    // reset items
    dispatch(policiesSetAgenticList(defaultResult))

    try {
        const result = await PoliciesService.getAgenticList(
            action.practice,
            action.payload.pagination,
            action.payload.filters,
            action.payload.sort,
            signal,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || defaultResult
    } catch (e) {
        action.onError?.()

        return defaultResult
    } finally {
        // Only stop loading if it wasn't aborted by other trigger
        if (!signal.aborted) {
            dispatch(runtimeStopLoading("policiesGetAgenticListLoading"))
        }
    }
})

/**
 * Get aggregations of medicaid reports by plan type
 */
export const policiesGetMedicaidReportsAggregationsPlanType = createAsyncThunk<
    ICoverageHistoryAggregationsMedicaidPlanType | null,
    IThunkActionWithPracticeData<{
        filters?: IPoliciesListFiltersData
    }>
>(
    "medicaidGetReportsAggregationsPlanTypePrefix",
    async (action, { dispatch, signal }) => {
        dispatch(
            runtimeStartLoading(
                "medicaidGetReportsAggregationsPlanTypeLoading",
            ),
        )

        try {
            const result =
                await PoliciesService.getMedicaidReportsAggregationPlanType(
                    action.practice,
                    action.payload.filters,
                    signal,
                )

            if (result) {
                action.onSuccess?.(result)
            } else {
                action.onError?.(result)
            }

            return result || null
        } catch (e) {
            action.onError?.()

            return null
        } finally {
            // Only stop loading if it wasn't aborted by other trigger
            if (!signal.aborted) {
                dispatch(
                    runtimeStopLoading(
                        "medicaidGetReportsAggregationsPlanTypeLoading",
                    ),
                )
            }
        }
    },
)

/**
 * Get aggregations of medicaid reports by active plan type
 */
export const policiesGetMedicaidReportsAggregationsActivePlanType =
    createAsyncThunk<
        ICoverageHistoryAggregationsMedicaidActivePlanType | null,
        IThunkActionWithPracticeData<{
            filters?: IPoliciesListFiltersData
        }>
    >(
        "medicaidGetReportsAggregationsActivePlanTypePrefix",
        async (action, { dispatch, signal }) => {
            dispatch(
                runtimeStartLoading(
                    "medicaidGetReportsAggregationsActivePlanTypeLoading",
                ),
            )

            try {
                const result =
                    await PoliciesService.getMedicaidReportsAggregationActivePlanType(
                        action.practice,
                        action.payload.filters,
                        signal,
                    )

                if (result) {
                    action.onSuccess?.(result)
                } else {
                    action.onError?.(result)
                }

                return result || null
            } catch (e) {
                action.onError?.()

                return null
            } finally {
                // Only stop loading if it wasn't aborted by other trigger
                if (!signal.aborted) {
                    dispatch(
                        runtimeStopLoading(
                            "medicaidGetReportsAggregationsActivePlanTypeLoading",
                        ),
                    )
                }
            }
        },
    )

/**
 * Get aggregations of plan year resets
 */
export const policiesGetPlanYearResetsAggregations = createAsyncThunk<
    ICoverageHistoryAggregationsPlanYearResets | null,
    IThunkActionWithPracticeData<{
        filters?: IPoliciesListFiltersData
    }>
>(
    "policiesGetPlanYearResetsAggregationsPrefix",
    async (action, { dispatch, signal }) => {
        dispatch(
            runtimeStartLoading("policiesGetPlanYearResetsAggregationsLoading"),
        )

        try {
            const result = await PoliciesService.getPlanYearResetsAggregations(
                action.practice,
                action.payload.filters,
                signal,
            )

            if (result) {
                action.onSuccess?.(result)
            } else {
                action.onError?.(result)
            }

            return result || null
        } catch (e) {
            action.onError?.()

            return null
        } finally {
            // Only stop loading if it wasn't aborted by other trigger
            if (!signal.aborted) {
                dispatch(
                    runtimeStopLoading(
                        "policiesGetPlanYearResetsAggregationsLoading",
                    ),
                )
            }
        }
    },
)

/**
 * Get list of assigned tasks
 */
export const policiesGetAssignedTasksList = createAsyncThunk<
    ICoverageHistoryListResult,
    IThunkActionWithPracticeData<{
        listType: PoliciesListType
        pagination: IPagination
        filters?: IPoliciesListFiltersData
        sort?: ICoverageHistoryListSort
    }>
>(
    "policiesGetAssignedTasksListPrefix",
    async (action, { dispatch, signal, getState }) => {
        dispatch(runtimeStartLoading("policiesGetAssignedTasksListLoading"))

        const defaultResult: ICoverageHistoryListResult = {
            items: [],
            newPagination: action.payload.pagination,
            isError: false,
        }

        // reset items
        dispatch(policiesSetAssignedTasksList(defaultResult))

        try {
            const globalState = getState() as RootState

            // Ensure that the relevant userDetails are loaded
            if (
                !globalState.userDetails.profile ||
                !globalState.userDetails.selectedPracticeRole
            ) {
                return defaultResult
            }

            // Users with manageTaskAssignments permission can filter by assignee
            // and if no filter is provided, then we will return all tasks
            // Users without this permission cannot filter and will only see their own tasks
            const hasManageTaskAssignmentsPermission =
                PermissionsHelper.hasPermission(
                    OneVerifyPermission.manageTaskAssignments,
                    globalState.userDetails.selectedPracticeRole,
                )

            const result = await PoliciesService.getAssignedTasksList(
                action.practice,
                action.payload.listType,
                action.payload.pagination,
                {
                    ...action.payload.filters,

                    ...(!hasManageTaskAssignmentsPermission
                        ? {
                              taskAssignmentAssignee: [
                                  globalState.userDetails.profile.id,
                              ],
                          }
                        : {}),
                },
                action.payload.sort,
                signal,
            )

            if (result) {
                action.onSuccess?.(result)
            } else {
                action.onError?.(result)
            }

            return result || defaultResult
        } catch (e) {
            action.onError?.()

            return defaultResult
        } finally {
            // Only stop loading if it wasn't aborted by other trigger
            if (!signal.aborted) {
                dispatch(
                    runtimeStopLoading("policiesGetAssignedTasksListLoading"),
                )
            }
        }
    },
)

/**
 * Get aggregations of reports by status codes
 */
export const policiesGetReportsAggregationsStatusCodes = createAsyncThunk<
    ICoverageHistoryAggregationsStatusCodes | null,
    IThunkActionWithPracticeData<{
        filters?: IPoliciesListFiltersData
    }>
>(
    "policiesGetReportsAggregationsStatusCodesPrefix",
    async (action, { dispatch, signal }) => {
        dispatch(
            runtimeStartLoading(
                "policiesGetReportsAggregationsStatusCodesLoading",
            ),
        )

        try {
            const result =
                await PoliciesService.getReportsAggregationStatusCodes(
                    action.practice,
                    action.payload.filters,
                    signal,
                )

            if (result) {
                action.onSuccess?.(result)
            } else {
                action.onError?.(result)
            }

            return result || null
        } catch (e) {
            action.onError?.()

            return null
        } finally {
            // Only stop loading if it wasn't aborted by other trigger
            if (!signal.aborted) {
                dispatch(
                    runtimeStopLoading(
                        "policiesGetReportsAggregationsStatusCodesLoading",
                    ),
                )
            }
        }
    },
)

/**
 * Get aggregations of reports by flags
 */
export const policiesGetReportsAggregationsFlags = createAsyncThunk<
    ICoverageHistoryAggregationsFlags | null,
    IThunkActionWithPracticeData<{
        filters?: IPoliciesListFiltersDataWithMedicaid
    }>
>(
    "policiesGetReportsAggregationsFlagsPrefix",
    async (action, { dispatch, signal }) => {
        dispatch(
            runtimeStartLoading("policiesGetReportsAggregationsFlagsLoading"),
        )

        try {
            const result = await PoliciesService.getReportsAggregationFlags(
                action.practice,
                action.payload.filters,
                signal,
            )

            if (result) {
                action.onSuccess?.(result)
            } else {
                action.onError?.(result)
            }

            return result || null
        } catch (e) {
            action.onError?.()

            return null
        } finally {
            // Only stop loading if it wasn't aborted by other trigger
            if (!signal.aborted) {
                dispatch(
                    runtimeStopLoading(
                        "policiesGetReportsAggregationsFlagsLoading",
                    ),
                )
            }
        }
    },
)

export const policiesResetSelectedItemAndRelatedData = createAsyncThunk(
    "policiesResetSelectedItemAndRelatedDataPrefix",
    async (_, { dispatch }) => {
        // Reset selected item and related data to avoid showing wrong data during loading
        dispatch(policiesResetSelectedItem())

        // Also reset selected item related override
        dispatch(coverageOverridesSetSelectedPolicyOverride(null))
    },
)

/**
 * Get single historical policy
 */
export const policiesGetSinglePolicy = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        triesCount?: number
        retryMaxCount?: number
        nirvanaRequestId: string
        withPolicyDataReFetch?: boolean
    }>
>("policiesGetSinglePolicyPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("policiesGetSinglePolicyLoading"))

    const triesCount = action.payload.triesCount || 0

    const { retryMaxCount, nirvanaRequestId, withPolicyDataReFetch } =
        action.payload

    try {
        dispatch(policiesResetSelectedItemAndRelatedData())

        const needRetry = !!retryMaxCount && triesCount <= retryMaxCount

        const result = await PoliciesService.getSingle(
            action.practice,
            nirvanaRequestId,
            withPolicyDataReFetch,
            needRetry ? [400] : undefined,
        )

        if (result) {
            dispatch(policiesSetSelectedItem(result))

            dispatch(
                policiesGetSinglePolicyRelatedHistory({
                    practice: action.practice,
                    payload: {
                        nirvanaRequestId,
                        pagination: UtilHelper.getPagination(),
                    },
                }),
            )

            action.onSuccess?.(result)
        } else {
            // Retry if needed
            if (needRetry) {
                await UtilHelper.sleep(1)

                dispatch(
                    policiesGetSinglePolicy({
                        ...action,

                        payload: {
                            ...action.payload,
                            triesCount: triesCount + 1,
                        },
                    }),
                )

                return
            }

            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("policiesGetSinglePolicyLoading"))
    }
})

/**
 * Retry single policy
 */
export const policiesRetrySinglePolicy = createAsyncThunk<
    IOneVerifyCoverageResult | null,
    IThunkActionWithPracticeData<{
        nirvanaRequestId: string
        patientId?: string
    }>
>("policiesRetrySinglePolicyPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("policiesRetrySinglePolicyLoading"))

    try {
        const result = await PoliciesService.retrySingle(
            action.practice,
            action.payload.nirvanaRequestId,
            action.payload.patientId,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || null
    } catch (e) {
        action.onError?.()

        return null
    } finally {
        dispatch(runtimeStopLoading("policiesRetrySinglePolicyLoading"))

        dispatch(
            userDetailsGetPracticeUsageQuota({
                payload: action.practice,
            }),
        )
    }
})

/**
 * Get single policy related history
 */
export const policiesGetSinglePolicyRelatedHistory = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        nirvanaRequestId: string
        pagination: IPagination
    }>
>(
    "policiesGetSinglePolicyRelatedHistoryPrefix",
    async (action, { dispatch }) => {
        dispatch(
            runtimeStartLoading("policiesGetSinglePolicyRelatedHistoryLoading"),
        )

        try {
            const result = await PoliciesService.getSingleRelatedHistory(
                action.practice,
                action.payload.nirvanaRequestId,
                action.payload.pagination,
            )

            if (result) {
                dispatch(policiesAddSelectedItemHistory(result))

                action.onSuccess?.(result)
            } else {
                action.onError?.(result)
            }
        } catch (e) {
            action.onError?.()
        } finally {
            dispatch(
                runtimeStopLoading(
                    "policiesGetSinglePolicyRelatedHistoryLoading",
                ),
            )
        }
    },
)

/**
 * Export all policies with applied filters and sort to CSV
 */
export const policiesExportList = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        viewType: PoliciesListType
        generatePolicyOnTheFly: boolean
    }>
>("policiesExportListPrefix", async (action, { dispatch, getState }) => {
    dispatch(runtimeStartLoading("policiesExportListLoading"))

    try {
        const fetchedItems = await dispatch(
            policiesGetFullList({
                practice: action.practice,
                payload: {
                    type: action.payload.viewType,
                    ignoreNPPESSearch: true,
                    generatePolicyOnTheFly:
                        action.payload.generatePolicyOnTheFly,
                },
            }),
        ).unwrap()

        const globalState = getState() as RootState

        const columns = PoliciesListHelper.getColumnsConfiguration(
            action.payload.viewType,
            globalState.userDetails.selectedPracticeRole?.availableModalities,
            true,
        )

        const csvData: any[][] = []

        // headers
        csvData.push(columns.map(item => item.label))

        // data
        for (const coverageCheck of fetchedItems) {
            const row: any[] = []

            for (const column of columns) {
                row.push(
                    ExportReportHelper.getMappedColumnValue(
                        coverageCheck,
                        column,
                        "",
                        false,
                    ),
                )
            }

            csvData.push(row)
        }

        CsvHelper.createAndDownloadCsvDocument(
            PapaParse.unparse(csvData),
            `${action.payload.viewType}-${POLICIES_CONFIG.exportPoliciesBaseName}`,
        )

        AmplitudeHelper.logEvent(AmplitudeEvent.EXPORTED_REPORT, {
            practiceId: action.practice.id,
            practiceName: action.practice.name,
            reportExportSuccess: fetchedItems.length > 0,
            reportType: action.payload.viewType,
            destination: PoliciesExportDestination.CSV,
        })

        if (fetchedItems.length) {
            action.onSuccess?.(fetchedItems)
        } else {
            action.onError?.(fetchedItems)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("policiesExportListLoading"))

        action.onFinally?.()
    }
})

/**
 * Get full list of policies of some type - based on filters and sort in state
 */
export const policiesGetFullList = createAsyncThunk<
    IOneVerifyCoverageCheckHistory[],
    IThunkActionWithPracticeData<
        IOneVerifyGetHistoryListAdditionalData & {
            type: PoliciesListType
        }
    >
>(
    "policiesGetFullListPrefix",
    async (
        action,
        { dispatch, getState },
    ): Promise<IOneVerifyCoverageCheckHistory[]> => {
        dispatch(policiesSetGettingFullListProgress(0))
        dispatch(runtimeStartLoading("policiesGetFullListLoading"))

        const globalState = getState() as RootState

        let progressIntervalId: NodeJS.Timeout | undefined
        const fetchedItems: IOneVerifyCoverageCheckHistory[] = []

        try {
            let result:
                | {
                      items: IOneVerifyCoverageCheckHistory[]
                      newPagination: IPagination
                      isError: boolean
                  }
                | undefined

            const neededPaginationLength =
                POLICIES_CONFIG.availablePaginationOptions[
                    POLICIES_CONFIG.availablePaginationOptions.length - 1
                ].value

            const additionalRequestData: IOneVerifyGetHistoryListAdditionalData =
                {
                    generatePolicyOnTheFly:
                        action.payload.generatePolicyOnTheFly,
                    ignoreNPPESSearch: action.payload.ignoreNPPESSearch,
                }

            const { filtersConfiguration, sortConfiguration } =
                PoliciesListHelper.getViewTypedStateData(
                    action.payload.type,
                    globalState.policies,
                )

            do {
                const newPagination =
                    result?.newPagination ||
                    UtilHelper.getPagination(neededPaginationLength)

                switch (action.payload.type) {
                    case PoliciesListType.REPORTS:
                        result = await PoliciesService.getReportsList(
                            action.practice,
                            newPagination,
                            filtersConfiguration,
                            sortConfiguration,
                            undefined,
                            additionalRequestData,
                        )

                        break

                    case PoliciesListType.HISTORY_SEARCH:
                        result = await PoliciesService.getSearchList(
                            action.practice,
                            newPagination,
                            filtersConfiguration,
                            sortConfiguration,
                            undefined,
                            additionalRequestData,
                        )

                        break

                    case PoliciesListType.OVERRIDES:
                        result = await PoliciesService.getOverridesList(
                            action.practice,
                            newPagination,
                            filtersConfiguration,
                            sortConfiguration,
                            undefined,
                            additionalRequestData,
                        )

                        break

                    case PoliciesListType.PLAN_YEAR_RESETS:
                        result = await PoliciesService.getPlanYearResetsList(
                            action.practice,
                            newPagination,
                            filtersConfiguration,
                            sortConfiguration,
                            undefined,
                            additionalRequestData,
                        )

                        break

                    case PoliciesListType.MEDICAID:
                        result = await PoliciesService.getMedicaidList(
                            action.practice,
                            newPagination,
                            filtersConfiguration,
                            sortConfiguration,
                            undefined,
                            additionalRequestData,
                        )

                        break

                    case PoliciesListType.AGENTIC:
                        result = await PoliciesService.getAgenticList(
                            action.practice,
                            newPagination,
                            filtersConfiguration,
                            sortConfiguration,
                        )

                        break

                    case PoliciesListType.TASK_ASSIGNMENTS:
                    case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
                    case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                        result =
                            await TaskAssignmentsService.getTaskAssignments(
                                action.practice,
                                action.payload.type,
                                newPagination,
                                filtersConfiguration,
                                sortConfiguration,
                            )

                        break

                    case PoliciesListType.ASSIGNED_TASKS:
                    case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
                    case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                        result = await PoliciesService.getAssignedTasksList(
                            action.practice,
                            action.payload.type,
                            newPagination,
                            filtersConfiguration,
                            sortConfiguration,
                        )

                        break
                }

                if (result?.items.length) {
                    fetchedItems.push(...result.items)
                }

                // Notify progress and set interval for auto increment of progress
                if (result?.newPagination.total) {
                    dispatch(
                        policiesSetGettingFullListProgress(
                            Math.max(
                                (getState() as RootState).policies
                                    .policiesGettingFullListProgress,
                                Math.floor(
                                    (fetchedItems.length /
                                        result.newPagination.total) *
                                        100,
                                ),
                            ),
                        ),
                    )

                    // done so it doesn't stuck on some value for single chunk export
                    // Also so it doesn't jump a lot
                    if (!progressIntervalId) {
                        // So calculations for interval below is approximate, based on average values and scaling factor.
                        //
                        // Time it takes to fetch records:
                        //     Total policies: 20000
                        //     100 policies -> 14000 ms
                        //
                        //     Total policies: 1000
                        //     100 policies -> around 4200 ms
                        //
                        // Interval:
                        //     Updates +1% every X seconds
                        //     We need interval:
                        //     total time to fetch all records approximately: (total / 100) * timeToFetch100
                        //
                        // If for total 1000 records it takes 42000ms to load, if for total 20000 records it takes 2800000ms. What would be formula by calculating total time for any amount of total records.
                        // Given that the time per record appears to increase as the total number of records increases, we need to consider a scaling factor. We can attempt to fit this relationship using a scaling law, such as a quadratic or polynomial relationship.
                        //
                        // The general formula to calculate the total time to load any number of records N is (got it through different calculations):
                        //
                        //     T(N) = 36.842 * N + 0.005158 * Math.pow(N, 2)
                        //
                        // Examples:
                        //     1000 -> 36.842 * N + 0.005158 * Math.pow(N, 2) = 42 * 1000 ms
                        //     5000 -> 36.842 * N + 0.005158 * Math.pow(N, 2) = 184210 + 0.005158 * 25000000 = 313 160 ms
                        //     20 000 -> 36.842 * N + 0.005158 * Math.pow(N, 2) = 736840 + 0.005158 * 400000000 = 2800040 ms
                        //
                        // And T(N) / 100 -> our interval time to approximately update 1% up

                        const progressIntervalValueMs =
                            (36.842 * result.newPagination.total +
                                0.005158 *
                                    Math.pow(result.newPagination.total, 2)) /
                            100

                        progressIntervalId = setInterval(() => {
                            dispatch(policiesIncreaseGetFullListProgress())
                        }, progressIntervalValueMs)
                    }
                }
            } while (result?.newPagination.moreAvailable)

            if (fetchedItems.length) {
                action.onSuccess?.(fetchedItems)
            } else {
                action.onError?.(fetchedItems)
            }

            return fetchedItems
        } catch (e) {
            return fetchedItems
        } finally {
            if (progressIntervalId) {
                clearInterval(progressIntervalId)
            }

            dispatch(policiesSetGettingFullListProgress(100))
            dispatch(runtimeStopLoading("policiesGetFullListLoading"))

            action.onFinally?.()
        }
    },
)

/**
 * Retry multiple policies
 */
export const retryBulkCheck = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        nirvanaRequestIds: string[]
        policyViewType: PoliciesListType
    }>
>(
    "policiesRetryBulkPolicyPrefix",
    async (action, { dispatch }): Promise<any> => {
        dispatch(runtimeStartLoading("policiesRetryBulkPolicyLoading"))

        let processedCount = 0

        try {
            while (processedCount < action.payload.nirvanaRequestIds.length) {
                // Take what the rate cap still has room for. Blocks only once
                // the allowance is fully spent.
                const allowedBatchSize =
                    await CheckerHelper.awaitBulkChecksCapacity(
                        Math.min(
                            CHECKER_CONFIG.processBatchSize,
                            action.payload.nirvanaRequestIds.length -
                                processedCount,
                        ),
                    )

                // Unreachable while the checker config is sane, but a
                // non-positive batch would never advance processedCount and
                // would hang the tab
                if (allowedBatchSize <= 0) {
                    break
                }

                const coverageChecks = [] as Promise<any>[]

                // Generate processing promises with our "pagination' based on allowedBatchSize
                for (
                    let i = processedCount;
                    i < processedCount + allowedBatchSize;
                    i++
                ) {
                    if (!action.payload.nirvanaRequestIds[i]) {
                        break
                    }

                    coverageChecks.push(
                        PoliciesService.retrySingle(
                            action.practice,
                            action.payload.nirvanaRequestIds[i],
                        ),
                    )
                }

                processedCount += allowedBatchSize

                await Promise.all(coverageChecks)
            }

            // Reset pagination back to first page
            const defaultResult: ICoverageHistoryListResult = {
                items: [],
                newPagination: UtilHelper.getPagination(
                    BrowserStorageHelper.get<ISelectRenderedOption>(
                        action.payload.policyViewType +
                            GENERAL_CONFIG.browserStorageKeys
                                .policiesResultsPerPageSuffix,
                        undefined,
                        BrowserStorageType.sessionStorage,
                    )?.value ??
                        POLICIES_CONFIG.availablePaginationOptions[0].value,
                ),
                isError: false,
            }

            switch (action.payload.policyViewType) {
                case PoliciesListType.HISTORY_SEARCH:
                    dispatch(policiesSetHistoryList(defaultResult))

                    break

                case PoliciesListType.REPORTS:
                    dispatch(policiesSetReportsList(defaultResult))

                    break

                case PoliciesListType.OVERRIDES:
                    dispatch(policiesSetOverridesList(defaultResult))

                    break

                case PoliciesListType.PLAN_YEAR_RESETS:
                    dispatch(policiesSetPlanYearResetsList(defaultResult))

                    break

                case PoliciesListType.MEDICAID:
                    dispatch(policiesSetMedicaidList(defaultResult))

                    break
            }

            action.onSuccess?.()
        } catch (e) {
            action.onError?.()
        } finally {
            dispatch(runtimeStopLoading("policiesRetryBulkPolicyLoading"))
        }
    },
)

/**
 * Write to EHR
 */
export const policiesWriteToEhr = createAsyncThunk<
    IOneVerifyCoverageCheckHistory | null,
    IThunkActionWithPracticeData<{
        nirvanaRequestId: string
        listType?: PoliciesListType
        oneVerifyInternalId: string
    }>
>("policiesWriteToEhrPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("policiesWriteToEhrLoading"))

    try {
        const result = await PoliciesService.writeToEhr(
            action.practice,
            action.payload.nirvanaRequestId,
            {
                oneVerifyInternalId: action.payload.oneVerifyInternalId,
            },
        )

        if (result?.policy?.ehrWriteLog) {
            // Update selected item
            dispatch(
                policiesUpdatedSelectedItemEhrWriteStatus({
                    policyId: result.policy.id,
                    ehrWriteLog: result.policy.ehrWriteLog,
                }),
            )

            // Update list items
            if (action.payload.listType) {
                dispatch(
                    policiesUpdateListItems({
                        listType: action.payload.listType,
                        updatedItems: [result],
                    }),
                )
            }

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || null
    } catch (e) {
        action.onError?.()

        return null
    } finally {
        dispatch(runtimeStopLoading("policiesWriteToEhrLoading"))
    }
})
